import type {
  ApiResponse,
  ContactEntryType,
  ContactIdentityResolution,
  PlatformEnum,
} from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError, type AxiosError } from 'axios';
import api from '@/libs/axiosInstance';

/**
 * Sentinel thrown by the resolve queryFn when the backend answers 200 with an
 * empty body — the benign "this contact has no identity yet" outcome, distinct
 * from a real transport/parse fault. A dedicated class lets
 * {@link isContactUnresolvedError} recognise it by instance instead of treating
 * every non-Axios throw (e.g. an interceptor TypeError) as "not resolved".
 */
export class ContactUnresolvedError extends Error {
  constructor(message = 'Contact identity not resolved') {
    super(message);
    this.name = 'ContactUnresolvedError';
  }
}

/**
 * Shared key so every consumer targets the same resolve cache entry.
 *
 * `platform` is part of the key: unlike `sourceCreatorId` (a provisioning hint
 * that never changes which person resolves), the platform selects WHICH person a
 * handle maps to — the same handle on Instagram and TikTok are two different
 * contacts, so they must not share a cache entry. Called without `platform` the
 * key is the 3-element prefix, which still prefix-matches every platform variant
 * for invalidation (the save-prompt re-resolve, which is platform-agnostic).
 */
export const contactResolveQueryKey = (
  entryType: ContactEntryType | null | undefined,
  id: string | null | undefined,
  platform?: PlatformEnum | null
) =>
  platform
    ? (['contact-resolve', entryType, id, platform] as const)
    : (['contact-resolve', entryType, id] as const);

/**
 * True when a resolve failure is the benign "this contact has no identity yet"
 * outcome — a 404 from the resolve endpoint, or the {@link ContactUnresolvedError}
 * thrown when the backend answers 200 with an empty body — rather than a real
 * fault (5xx, network error, 403 no-brand-context, or an interceptor TypeError).
 * Only the benign case lets the rail offer its "save this creator" path; every
 * other failure must surface the drawer's error/retry state instead of
 * pretending the creator is simply unsaved.
 */
export const isContactUnresolvedError = (error: unknown): boolean => {
  if (isAxiosError(error)) return error.response?.status === 404;
  return error instanceof ContactUnresolvedError;
};

/**
 * A contact entry to resolve into a creator identity.
 * - `affiliate`: `id` is the affiliate user id (uuid), resolved brand-scoped.
 * - `creator`: `id` is the platform username; `platform` scopes the lookup.
 * - `platform`: which platform the `creator` handle belongs to. Omitted for the
 *   affiliate path (the backend ignores it there).
 * - `sourceCreatorId`: optional dataset creator id carried from search /
 *   recommendations. Lets the backend provision an identity for a creator that
 *   is not yet wishlisted, so the card resolves for a fresh discovery result.
 */
export interface ContactResolveRef {
  entryType: ContactEntryType;
  id: string;
  platform?: PlatformEnum;
  sourceCreatorId?: string;
}

/**
 * Fetch a contact resolution outside the single-ref hook. Exported for
 * multi-recipient flows (collab wizard bulk) that resolve N entries through
 * `useQueries` while sharing this cache via `contactResolveQueryKey`.
 */
export const fetchContactResolution = async (
  ref: ContactResolveRef
): Promise<ContactIdentityResolution> => {
  // `sourceCreatorId` only rides in the request params, never the query key, so
  // invalidation by `['contact-resolve', entryType, id]` keeps matching. `platform`
  // rides in both (it selects the resolved person, so it belongs in the key).
  const params: Record<string, string> = { entryType: ref.entryType, id: ref.id };
  if (ref.platform) params.platform = ref.platform;
  if (ref.sourceCreatorId) params.sourceCreatorId = ref.sourceCreatorId;

  const { data } = await api.get<ApiResponse<ContactIdentityResolution>>(
    '/dashboard/contacts/resolve',
    { params }
  );

  if (!data.data) {
    throw new ContactUnresolvedError();
  }

  return data.data;
};

/**
 * Resolves a contact entry (affiliate user id / creator username) into a
 * `creatorIdentityId` plus a minimal header. The id becomes the contactId /
 * query key the profile slice keys on.
 *
 * Disabled until both `entryType` and `id` are present, so it never fires while
 * the drawer is closed or the entry discriminant is missing (graceful: callers
 * fall back to the mock header).
 */
export const useResolveContactQuery = (ref: ContactResolveRef | null) =>
  useQuery<ContactIdentityResolution, Error>({
    queryKey: contactResolveQueryKey(ref?.entryType, ref?.id, ref?.platform),
    queryFn: () => fetchContactResolution(ref!),
    enabled: !!ref?.entryType && !!ref?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    // A 404/403 is a normal "not resolved / no access" outcome (e.g. an unsaved
    // discovery creator), not a transient fault. Retrying it would keep the
    // drawer in its loading state through the default backoff (~7s) before the
    // save prompt appears, and fire redundant requests. Only retry real faults.
    retry: (failureCount, error) => {
      const status = (error as AxiosError)?.response?.status;
      if (status === 404 || status === 403) return false;
      return failureCount < 1;
    },
    // A dozen observers across the drawer share this one query, and several of
    // them sit inside subtrees the drawer swaps in and out while it resolves.
    // Left on its default, TanStack re-attempts a failed resolution every time
    // one of those subtrees mounts — and since the panel shows its skeleton
    // again while the query is fetching, that mount unmounts the subtree that
    // caused it, which mounts it again on the next failure: one request per
    // cycle, forever. A resolution that failed must stay failed until someone
    // asks for it again. The explicit re-resolve paths are unaffected: the
    // drawer's retry button calls `refetch()` and the wishlist star invalidates
    // the key, and neither goes through a mount.
    retryOnMount: false,
  });
